import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@rq-ratings/pro-solid-svg-icons'
import { INotyfOptions, Notyf } from 'notyf'
import { NotyfNotification } from 'notyf/notyf.models'
import { DeepPartial, INotyfNotificationOptions } from 'notyf/notyf.options'
import React, { useContext } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import invariant from 'tiny-invariant'

import { TOAST_AUTOCLOSE_DELAY } from '../lib/constants'
import { useCaptureErrorShownToUserEvent } from './useCaptureErrorShownToUserEvent'

interface IRqNotyfNotificationOptions extends INotyfNotificationOptions {
  withoutReporting?: boolean
}

interface RqNotyf extends Notyf {
  // Same as notyf.error() except it doesn't report the error in PostHog/Sentry.
  // See method implementation further down for details.
  errorWithoutReporting: Notyf['error']

  open(options: DeepPartial<IRqNotyfNotificationOptions>): NotyfNotification
}

let notyf: RqNotyf | null = null

function useNotyf() {
  const captureErrorShownToUserEvent = useCaptureErrorShownToUserEvent()

  // Create a monkey-patched Notyf instance so that whenever the notyf.error()
  // method is called, we capture it as an event in PostHog & Sentry.
  function createNotyfInstance() {
    if (!notyf) {
      notyf = new Notyf(NOTYF_OPTIONS) as RqNotyf

      const originalNotyfOpenHandler = notyf.open

      // Monkey-patch the `open` method to capture events in PostHog & Sentry
      // when an error notification is shown to the user
      notyf.open = (options) => {
        const shouldReportError =
          options.type === 'error' && options.withoutReporting !== true

        if (shouldReportError) {
          captureErrorShownToUserEvent()
        }

        return originalNotyfOpenHandler.call(notyf, options)
      }

      // Add a custom errorWithoutReporting method for when we want to show
      // an error toast but don't want to report it to PostHog/Sentry.
      notyf.errorWithoutReporting = (payload) => {
        const commonOptions: DeepPartial<IRqNotyfNotificationOptions> = {
          type: 'error',
          withoutReporting: true,
        }

        const options: DeepPartial<IRqNotyfNotificationOptions> =
          typeof payload === 'string'
            ? { message: payload, ...commonOptions }
            : { ...payload, ...commonOptions }

        invariant(notyf, 'Expected notyf to be defined')

        return notyf.open(options)
      }
    }

    return notyf
  }

  const notyfInstance = createNotyfInstance()
  const context = React.createContext(notyfInstance)

  return useContext(context)
}

const NOTYF_OPTIONS: Partial<INotyfOptions> = {
  duration: TOAST_AUTOCLOSE_DELAY,
  position: { x: 'right', y: 'top' },
  types: [
    {
      type: 'default',
      background: '#3B7DDD',
      icon: { className: 'notyf__icon--success', tagName: 'i' },
    },
    {
      type: 'info',
      background: '#3B7DDD',
      icon: renderToStaticMarkup(<FontAwesomeIcon icon={faCircleInfo} />),
    },
    {
      type: 'success',
      background: '#28a745',
      icon: { className: 'notyf__icon--success', tagName: 'i' },
    },
    {
      type: 'warning',
      background: '#ffc107',
      icon: { className: 'notyf__icon--error', tagName: 'i' },
    },
    {
      type: 'danger',
      background: '#dc3545',
      icon: { className: 'notyf__icon--error', tagName: 'i' },
    },
  ],
}

export default useNotyf
